// src/hooks/useBookingsFilter.ts
import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface BookingsFilterState {
  searchText?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isProspect?: boolean;
  isExpired?: boolean;
  isConverted?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface BookingsFilterParams {
  searchText?: string;
  clientId?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isProspect?: boolean;
  isExpired?: boolean;
  isConverted?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface UseBookingsFilterReturn {
  filterState: BookingsFilterState;
  setFilter: (key: keyof BookingsFilterState, value: any) => void;
  setPageNumber: (page: number) => void;
  commitFilters: (state: BookingsFilterState) => void;
  clearFilters: () => void;
  applyFilters: () => void;
  activeFilterCount: number;
  isFilterActive: boolean;
  getFilterParams: () => BookingsFilterParams;
}

// Parse a URL search-param value into a tri-state boolean: 'true' -> true,
// 'false' -> false, anything else (including missing) -> undefined.
const parseBoolParam = (raw: string | null): boolean | undefined => {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
};

// statusPreset: when supplied (e.g. "Quotation" for the /quotes route), this
// status is applied as the initial value AND re-applied on clearFilters,
// so the preset survives the user hitting "clear". The drawer can still
// override it for the duration of an active filter session, but clear
// always returns the list to its preset.
//
// defaultSortBy: same idea for the sort field. When supplied (e.g. "created"
// for /quotes), it seeds the initial sort and is restored on clear, and is
// not counted as an "active" filter in the badge.
//
// defaultIsProspect: same idea for the prospect filter. Used by /quotes to
// default the list to prospects only. Drawer "All clients" / "Existing only"
// override during a session; clear restores the preset.
export const useBookingsFilter = (
  clientId?: number,
  statusPreset?: string,
  defaultSortBy?: string,
  defaultIsProspect?: boolean
): UseBookingsFilterReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filterState, setFilterState] = useState<BookingsFilterState>(() => {
    const urlIsProspect = parseBoolParam(searchParams.get('isProspect'));
    return {
      searchText: searchParams.get('searchText') || undefined,
      status: searchParams.get('status') || statusPreset || undefined,
      startDate: searchParams.get('startDate') || undefined,
      endDate: searchParams.get('endDate') || undefined,
      sortBy: searchParams.get('sortBy') || defaultSortBy || undefined,
      sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
      isProspect:
        urlIsProspect !== undefined ? urlIsProspect : defaultIsProspect,
      isExpired: parseBoolParam(searchParams.get('isExpired')),
      isConverted: parseBoolParam(searchParams.get('isConverted')),
      pageNumber: parseInt(searchParams.get('pageNumber') || '1'),
      pageSize: parseInt(searchParams.get('pageSize') || '100'),
    };
  });

  const setFilter = useCallback(
    (key: keyof BookingsFilterState, value: any) => {
      setFilterState((prev) => ({ ...prev, [key]: value, pageNumber: 1 }));
    },
    []
  );

  const setPageNumber = useCallback(
    (page: number) => {
      setFilterState((prev) => ({ ...prev, pageNumber: page }));
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('pageNumber', String(page));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const commitFilters = useCallback(
    (state: BookingsFilterState) => {
      setFilterState(state);
      const newParams = new URLSearchParams();
      Object.entries(state).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          newParams.set(key, String(value));
        }
      });
      setSearchParams(newParams);
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    const clearedState: BookingsFilterState = {
      pageNumber: 1,
      pageSize: 100,
      sortOrder: 'desc',
      // Preserve presets across clears.
      status: statusPreset || undefined,
      sortBy: defaultSortBy || undefined,
      isProspect: defaultIsProspect,
      isExpired: undefined,
      isConverted: undefined,
    };
    setFilterState(clearedState);
    const newParams = new URLSearchParams(searchParams);
    [
      'searchText',
      'status',
      'startDate',
      'endDate',
      'sortBy',
      'sortOrder',
      'isProspect',
      'isExpired',
      'isConverted',
    ].forEach((k) => newParams.delete(k));
    if (statusPreset) newParams.set('status', statusPreset);
    if (defaultSortBy) newParams.set('sortBy', defaultSortBy);
    if (defaultIsProspect !== undefined)
      newParams.set('isProspect', String(defaultIsProspect));
    newParams.set('pageNumber', '1');
    newParams.set('pageSize', '100');
    setSearchParams(newParams);
  }, [
    searchParams,
    setSearchParams,
    statusPreset,
    defaultSortBy,
    defaultIsProspect,
  ]);

  const applyFilters = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(filterState).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }, [filterState, searchParams, setSearchParams]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterState.searchText) count++;
    // Status, sort, and isProspect count as "active" only when they
    // differ from the preset.
    if (filterState.status && filterState.status !== statusPreset) count++;
    if (filterState.startDate) count++;
    if (filterState.endDate) count++;
    if (filterState.sortBy && filterState.sortBy !== defaultSortBy) count++;
    if (filterState.isProspect !== defaultIsProspect) count++;
    if (filterState.isExpired !== undefined) count++;
    if (filterState.isConverted !== undefined) count++;
    return count;
  }, [filterState, statusPreset, defaultSortBy, defaultIsProspect]);

  const isFilterActive = activeFilterCount > 0;

  const getFilterParams = useCallback((): BookingsFilterParams => {
    const params: BookingsFilterParams = {};
    if (filterState.searchText) params.searchText = filterState.searchText;
    if (clientId) params.clientId = clientId;
    if (filterState.status) params.status = filterState.status;
    if (filterState.startDate) params.startDate = filterState.startDate;
    if (filterState.endDate) params.endDate = filterState.endDate;
    if (filterState.sortBy) params.sortBy = filterState.sortBy;
    if (filterState.sortOrder) params.sortOrder = filterState.sortOrder;
    if (filterState.isProspect !== undefined)
      params.isProspect = filterState.isProspect;
    if (filterState.isExpired !== undefined)
      params.isExpired = filterState.isExpired;
    if (filterState.isConverted !== undefined)
      params.isConverted = filterState.isConverted;
    if (filterState.pageNumber) params.pageNumber = filterState.pageNumber;
    if (filterState.pageSize) params.pageSize = filterState.pageSize;
    return params;
  }, [filterState, clientId]);

  return {
    filterState,
    setFilter,
    setPageNumber,
    commitFilters,
    clearFilters,
    applyFilters,
    activeFilterCount,
    isFilterActive,
    getFilterParams,
  };
};
